import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Users,
  Calendar, Clock, Plus, Play, Square, Settings, 
  Monitor, BarChart3, RefreshCw, ChevronRight, UserPlus,
  Check, X, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TelehealthVideoModule = () => {
  const [activeTab, setActiveTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [visitTypes, setVisitTypes] = useState([]);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // New session form
  const [newSession, setNewSession] = useState({
    patient_id: '',
    patient_name: '',
    provider_id: '',
    provider_name: '',
    scheduled_time: '',
    duration_minutes: 30,
    visit_type: 'routine',
    recording_enabled: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sessionsRes, analyticsRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/phase34/video/rooms`),
        fetch(`${API_URL}/api/phase34/video/analytics`),
        fetch(`${API_URL}/api/phase34/video/visit-types`)
      ]);

      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.rooms || []);
      }
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (typesRes.ok) {
        const data = await typesRes.json();
        setVisitTypes(data.visit_types || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load telehealth data');
    }
    setLoading(false);
  };

  const createSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase34/video/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSession)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Session scheduled successfully');
        setShowNewSession(false);
        setNewSession({
          patient_id: '',
          patient_name: '',
          provider_id: '',
          provider_name: '',
          scheduled_time: '',
          duration_minutes: 30,
          visit_type: 'routine',
          recording_enabled: false
        });
        fetchData();
        
        // Show join info
        toast.info(`Access Code: ${data.access_code}`, { duration: 10000 });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      toast.error('Error creating session');
    }
  };

  const updateSessionStatus = async (roomId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/phase34/video/rooms/${roomId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Session ${status}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Error updating session');
    }
  };

  const cancelSession = async (roomId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase34/video/rooms/${roomId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'User cancelled', cancelled_by: 'admin' })
      });

      if (response.ok) {
        toast.success('Session cancelled');
        fetchData();
      }
    } catch (error) {
      toast.error('Error cancelling session');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-700';
      case 'waiting': return 'bg-yellow-100 text-yellow-700';
      case 'in_progress': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-gray-100 text-gray-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="telehealth-video-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="h-7 w-7 text-teal-600" />
            Telehealth Video Center
          </h1>
          <p className="text-gray-600 mt-1">Video conferencing for hospice telehealth visits</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
            <DialogTrigger asChild>
              <Button data-testid="new-session-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Schedule Video Session</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Patient ID</Label>
                    <Input
                      value={newSession.patient_id}
                      onChange={(e) => setNewSession({...newSession, patient_id: e.target.value})}
                      placeholder="Enter patient ID"
                      data-testid="input-patient-id"
                    />
                  </div>
                  <div>
                    <Label>Patient Name</Label>
                    <Input
                      value={newSession.patient_name}
                      onChange={(e) => setNewSession({...newSession, patient_name: e.target.value})}
                      placeholder="Patient name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Provider ID</Label>
                    <Input
                      value={newSession.provider_id}
                      onChange={(e) => setNewSession({...newSession, provider_id: e.target.value})}
                      placeholder="Provider ID"
                      data-testid="input-provider-id"
                    />
                  </div>
                  <div>
                    <Label>Provider Name</Label>
                    <Input
                      value={newSession.provider_name}
                      onChange={(e) => setNewSession({...newSession, provider_name: e.target.value})}
                      placeholder="Provider name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Scheduled Time</Label>
                    <Input
                      type="datetime-local"
                      value={newSession.scheduled_time}
                      onChange={(e) => setNewSession({...newSession, scheduled_time: e.target.value})}
                      data-testid="input-scheduled-time"
                    />
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Select
                      value={String(newSession.duration_minutes)}
                      onValueChange={(v) => setNewSession({...newSession, duration_minutes: parseInt(v)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="20">20 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Visit Type</Label>
                  <Select
                    value={newSession.visit_type}
                    onValueChange={(v) => setNewSession({...newSession, visit_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visitTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({type.default_duration} min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewSession(false)}>Cancel</Button>
                <Button onClick={createSession} data-testid="create-session-btn">
                  Schedule Session
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-700">{analytics?.total_sessions || 0}</p>
              </div>
              <Video className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{analytics?.completed_sessions || 0}</p>
              </div>
              <Check className="h-8 w-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-2xl font-bold text-red-600">{analytics?.cancelled_sessions || 0}</p>
              </div>
              <X className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completion Rate</p>
                <p className="text-2xl font-bold text-teal-600">{analytics?.completion_rate || 0}%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-teal-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Duration</p>
                <p className="text-2xl font-bold text-blue-600">{analytics?.average_duration_minutes || 0} min</p>
              </div>
              <Clock className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Video Sessions</CardTitle>
              <CardDescription>Manage telehealth video sessions</CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Video className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No video sessions scheduled</p>
                  <Button className="mt-4" onClick={() => setShowNewSession(true)}>
                    Schedule First Session
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <Video className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium">{session.patient_name || session.patient_id}</p>
                          <p className="text-sm text-gray-500">
                            Provider: {session.provider_name || session.provider_id}
                          </p>
                          <p className="text-sm text-gray-500">
                            {session.scheduled_time ? new Date(session.scheduled_time).toLocaleString() : 'Not scheduled'} • {session.duration_minutes} min
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(session.status)}>
                          {session.status?.toUpperCase()}
                        </Badge>
                        {session.status === 'scheduled' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => updateSessionStatus(session.id, 'in_progress')}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Start
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cancelSession(session.id)}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {session.status === 'in_progress' && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => updateSessionStatus(session.id, 'completed')}
                          >
                            <Square className="h-4 w-4 mr-1" />
                            End
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-teal-600" />
                Visit Types & Scheduling
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {visitTypes.map((type) => (
                  <div
                    key={type.id}
                    className="p-4 border rounded-lg hover:border-teal-300 hover:bg-teal-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setNewSession(prev => ({...prev, visit_type: type.id, duration_minutes: type.default_duration}));
                      setShowNewSession(true);
                    }}
                  >
                    <h4 className="font-medium">{type.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Default: {type.default_duration} minutes
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Active Sessions */}
          <Card>
            <CardHeader>
              <CardTitle>Active & Upcoming Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.filter(s => ['scheduled', 'waiting', 'in_progress'].includes(s.status)).length === 0 ? (
                <p className="text-center text-gray-500 py-4">No active sessions</p>
              ) : (
                <div className="space-y-2">
                  {sessions
                    .filter(s => ['scheduled', 'waiting', 'in_progress'].includes(s.status))
                    .map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{session.patient_name || session.patient_id}</p>
                          <p className="text-sm text-gray-500">
                            {session.scheduled_time ? new Date(session.scheduled_time).toLocaleTimeString() : 'Now'}
                          </p>
                        </div>
                        <Badge className={getStatusColor(session.status)}>{session.status}</Badge>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Visit Type Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.visit_type_breakdown && Object.keys(analytics.visit_type_breakdown).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.visit_type_breakdown).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                        <Badge variant="outline">{count} sessions</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Session Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Minutes</span>
                    <span className="font-bold">{analytics?.total_minutes || 0} min</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Completion Rate</span>
                    <span className="font-bold text-green-600">{analytics?.completion_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Duration</span>
                    <span className="font-bold">{analytics?.average_duration_minutes || 0} min</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Providers */}
          {analytics?.top_providers?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top Providers by Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analytics.top_providers.map((provider, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold">
                          {idx + 1}
                        </div>
                        <span>{provider.name}</span>
                      </div>
                      <Badge>{provider.sessions} sessions</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TelehealthVideoModule;
